import {
  TRANSFORMATION_STATUS_LABELS,
  type StockTransformation,
} from "@artisancode/api-types";
import { Calendar, CheckCircle2, Coffee, Eye, XCircle } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { formatDateWIB } from "@/lib/date-utils";
import { formatCurrency, formatCurrencyRounded } from "@/lib/utils";

import { summarizeProduction } from "./production-utils";

import type { ProductionRowsProps } from "./production-desktop-table";

export function ProductionMobileList({
  rows,
  ...actions
}: ProductionRowsProps) {
  if (rows.length === 0) {
    return (
      <div className="p-6 text-center text-xs text-muted-foreground">
        Tidak ada batch produksi yang sesuai dengan filter.
      </div>
    );
  }

  return (
    <div className="space-y-3 p-3">
      {rows.map((item) => (
        <ProductionCard key={item.id} item={item} {...actions} />
      ))}
    </div>
  );
}

function ProductionCard({
  item,
  canComplete,
  isCompleting,
  onViewDetail,
  onComplete,
  onCancelRequest,
}: Omit<ProductionRowsProps, "rows"> & { item: StockTransformation }) {
  const statusMeta = TRANSFORMATION_STATUS_LABELS[item.status];
  const { menus, totalInputCost, menuNames } = summarizeProduction(item);
  const uomName = menus[0]?.outputLine.uomName ?? "";

  return (
    <Card className="gap-2 py-3 shadow-xs">
      <CardContent className="space-y-2.5 px-4 text-xs">
        <div className="flex items-start justify-between gap-2">
          <div>
            <span className="font-mono text-sm font-bold text-foreground">
              {item.code}
            </span>
            <p className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
              <Calendar className="h-3 w-3" />
              {formatDateWIB(item.date)}
            </p>
          </div>
          <Badge
            variant={statusMeta.variant}
            className="shrink-0 text-[10px] capitalize"
          >
            {statusMeta.label}
          </Badge>
        </div>

        <div className="space-y-1 rounded-md bg-muted/40 p-2.5">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
            <Coffee className="h-3.5 w-3.5 shrink-0 text-primary" />
            <span>{menuNames || "Minuman"}</span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            {menus.length} menu
          </p>
        </div>

        <div className="grid grid-cols-3 gap-2 text-xs">
          <Stat label="Output Jadi" valueClassName="font-bold text-primary">
            {menus.length > 0
              ? `${item.totalOutputQty.toLocaleString("id-ID")} ${uomName}`
              : "-"}
          </Stat>
          <Stat label="HPP Satuan">
            {menus.length === 1 && menus[0]
              ? formatCurrency(menus[0].unitCost)
              : "Per menu"}
          </Stat>
          <Stat label="Total Biaya">
            {formatCurrencyRounded(totalInputCost)}
          </Stat>
        </div>

        {item.note && (
          <p className="rounded bg-muted/20 p-2 text-[11px] italic text-muted-foreground">
            {item.note}
          </p>
        )}

        <div className="flex items-center justify-end gap-2 border-t border-border/40 pt-2.5">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onViewDetail(item)}
            className="min-h-[40px] gap-1.5 text-xs"
          >
            <Eye className="h-3.5 w-3.5" />
            Rincian
          </Button>

          {item.status === "draft" && canComplete && (
            <Button
              size="sm"
              onClick={() => onComplete(item.id)}
              disabled={isCompleting}
              className="min-h-[40px] gap-1.5 bg-emerald-600 text-xs text-white hover:bg-emerald-700"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Selesaikan
            </Button>
          )}

          {item.status !== "cancelled" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onCancelRequest(item)}
              className="min-h-[40px] text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <XCircle className="mr-1 h-3.5 w-3.5" />
              Batalkan
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({
  label,
  valueClassName = "font-semibold",
  children,
}: {
  label: string;
  valueClassName?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded border p-2">
      <span className="block text-[10px] text-muted-foreground">{label}</span>
      <span className={`font-mono text-xs ${valueClassName}`}>{children}</span>
    </div>
  );
}
